import { type Task, TaskStatus, toDateString } from "@taskfortress/core"

export interface DaySection {
  date: string
  tasks: Task[]
}

export interface TasksByStatus {
  todo: Task[]
  done: Task[]
}

/**
 * Groups tasks by their status (TODO and DONE)
 */
export const groupTasksByStatus = (tasks: Task[]): TasksByStatus => {
  const todoTasks = tasks.filter((task) => task.status === TaskStatus.TODO)
  const doneTasks = tasks.filter((task) => task.status === TaskStatus.DONE)

  // Sort by index within each status group
  todoTasks.sort((a, b) => a.index - b.index)
  doneTasks.sort((a, b) => a.index - b.index)

  return {
    todo: todoTasks,
    done: doneTasks,
  }
}

/**
 * Groups tasks by their due date and returns a Record mapping date strings to task arrays
 * Tasks are sorted by status (TODO first, then DONE) and by index within each status
 */
export const groupTasksByDate = (tasks: Task[]): Record<string, Task[]> => {
  const grouped: Record<string, Task[]> = {}

  // Group tasks by their due date
  tasks.forEach((task) => {
    if (!task.dueDate) return

    const dateKey = toDateString(task.dueDate)
    if (!grouped[dateKey]) {
      grouped[dateKey] = []
    }
    grouped[dateKey].push(task)
  })

  // Sort tasks by status (todo first, then done) and by index within each status
  Object.keys(grouped).forEach((date) => {
    grouped[date].sort((a, b) => {
      // First sort by status (TODO before DONE)
      if (a.status !== b.status) {
        return a.status === TaskStatus.TODO ? -1 : 1
      }
      // Then sort by index within the same status
      return a.index - b.index
    })
  })

  return grouped
}

/**
 * Creates day sections for a given date range, with tasks grouped by date
 * Returns an array of DaySection objects, one for each date in the range
 */
export const createDaySections = (
  dateRange: string[],
  tasks: Task[],
): DaySection[] => {
  const tasksByDate = groupTasksByDate(tasks)

  return dateRange.map((date) => ({
    date,
    tasks: tasksByDate[date] || [],
  }))
}

/**
 * Gets tasks for a specific date from the grouped tasks
 */
export const getTasksForDate = (
  tasksByDate: Record<string, Task[]>,
  date: string,
): Task[] => {
  return tasksByDate[date] || []
}
